/**
 * Entra ID OIDC service for the admin portal.
 *
 * Handles the frontend side of the Authorization Code flow with PKCE:
 * - Generating PKCE parameters (code_verifier, code_challenge)
 * - Generating cryptographic state and nonce for CSRF/replay protection
 * - Storing PKCE state in sessionStorage
 * - Validating the callback and extracting the authorization code
 * - Constructing the Entra ID logout URL
 *
 * Requirements: 2.1, 2.2, 2.3, 2.4, 2.5, 2.7, 2.8, 7.2, 12.1, 12.2, 12.3
 */

import type { AuthConfig } from '@/types/auth-config';

// Session storage key for PKCE state (cleared after callback)
const PKCE_STORAGE_KEY = 'xs_entra_pkce';

interface PkceState {
  codeVerifier: string;
  state: string;
  nonce: string;
}

/**
 * Generates a cryptographically random hex string.
 * Uses Web Crypto API for secure randomness.
 */
function generateRandomString(byteLength: number): string {
  const array = new Uint8Array(byteLength);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Derives the S256 code challenge from a code verifier.
 * SHA-256 hash → base64url encoding (no padding).
 */
async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

class EntraIdService {
  /**
   * Initiate the OIDC Authorization Code flow with PKCE.
   * Generates PKCE parameters, stores them in sessionStorage,
   * and redirects the user to the Entra ID authorize endpoint.
   */
  async initiateLogin(config: AuthConfig): Promise<void> {
    if (!config.tenantId || !config.clientId || !config.redirectUri) {
      throw new Error('Entra ID configuration is incomplete');
    }

    // Generate 64-byte code_verifier (128 hex chars)
    const codeVerifier = generateRandomString(64);
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    // Generate 32-byte state and nonce (64 hex chars each)
    const state = generateRandomString(32);
    const nonce = generateRandomString(32);

    // Store PKCE state for validation on callback
    const pkceState: PkceState = { codeVerifier, state, nonce };
    sessionStorage.setItem(PKCE_STORAGE_KEY, JSON.stringify(pkceState));

    const params = new URLSearchParams({
      client_id: config.clientId,
      response_type: 'code',
      redirect_uri: config.redirectUri,
      scope: 'openid profile email',
      response_mode: 'query',
      state,
      nonce,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    });

    const authorizeUrl =
      `https://login.microsoftonline.com/${config.tenantId}/oauth2/v2.0/authorize?${params.toString()}`;

    window.location.href = authorizeUrl;
  }

  /**
   * Handle the OIDC callback after Entra ID redirects back.
   * Validates state, extracts the authorization code, and returns
   * the parameters needed for the backend token exchange.
   */
  handleCallback(searchParams: URLSearchParams): {
    code: string;
    codeVerifier: string;
    nonce: string;
  } {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const error = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    // Handle error response from Entra ID
    if (error) {
      sessionStorage.removeItem(PKCE_STORAGE_KEY);
      throw new Error(errorDescription || `Authentication failed: ${error}`);
    }

    if (!code || !state) {
      sessionStorage.removeItem(PKCE_STORAGE_KEY);
      throw new Error('Missing authorization code or state parameter');
    }

    // Retrieve and validate PKCE state
    const raw = sessionStorage.getItem(PKCE_STORAGE_KEY);
    sessionStorage.removeItem(PKCE_STORAGE_KEY);

    if (!raw) {
      throw new Error('PKCE state not found — possible CSRF attack or expired session');
    }

    const pkceState: PkceState = JSON.parse(raw);

    // Validate state parameter to prevent CSRF attacks
    if (pkceState.state !== state) {
      throw new Error('State parameter mismatch — possible CSRF attack');
    }

    return {
      code,
      codeVerifier: pkceState.codeVerifier,
      nonce: pkceState.nonce,
    };
  }

  /**
   * Build the Entra ID logout URL for single sign-out.
   */
  getLogoutUrl(tenantId: string, postLogoutRedirectUri: string): string {
    const params = new URLSearchParams({
      post_logout_redirect_uri: postLogoutRedirectUri,
    });
    return `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/logout?${params.toString()}`;
  }

  /**
   * Clean up any PKCE state from sessionStorage.
   */
  cleanup(): void {
    sessionStorage.removeItem(PKCE_STORAGE_KEY);
  }
}

export const entraIdService = new EntraIdService();
